import { useGetList, useTranslate } from "ra-core";
import { Fragment, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { AutomationRule } from "../../../../supabase/functions/_shared/automation/types.ts";
import { runActions, safeCode } from "./ruleModel";
import { selectClass } from "./ConditionBuilder";

const fmt = new Intl.DateTimeFormat("fr-CA", {
  timeZone: "America/Toronto",
  dateStyle: "short",
  timeStyle: "short",
});
const STATUSES = ["done", "skipped", "failed"] as const;

type Run = {
  id: number;
  rule_id: number;
  status: string;
  error?: string | null;
  detail?: unknown;
  created_at: string;
};

/**
 * Admin-only automation_runs history. Shows status and error codes only; every value is
 * rendered as React text (escaped), and payloads/traces in `detail` are never displayed.
 */
export const RunsHistory = ({
  rules,
}: {
  rules: readonly AutomationRule[];
}) => {
  const t = useTranslate();
  const [ruleId, setRuleId] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState<number | null>(null);
  const filter = {
    ...(ruleId ? { rule_id: Number(ruleId) } : {}),
    ...(status ? { status } : {}),
  };
  const { data: runs = [] } = useGetList<Run>(
    "automation_runs",
    {
      filter,
      pagination: { page: 1, perPage: 100 },
      sort: { field: "created_at", order: "DESC" },
    },
    { retry: false },
  );
  const statusLabel = (s: string) =>
    t(`gavion.automation.statuses.${s}`, { _: safeCode(s) ?? "" });
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2 sm:flex-row">
        <select
          aria-label={t("gavion.automation.history.filter_rule")}
          className={selectClass}
          value={ruleId}
          onChange={(e) => setRuleId(e.target.value)}
        >
          <option value="">{t("gavion.automation.history.all")}</option>
          {rules.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <select
          aria-label={t("gavion.automation.history.filter_status")}
          className={selectClass}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">{t("gavion.automation.history.all")}</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {statusLabel(s)}
            </option>
          ))}
        </select>
      </div>
      {runs.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("gavion.automation.history.none")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("gavion.automation.columns.date")}</TableHead>
                <TableHead>{t("gavion.automation.columns.name")}</TableHead>
                <TableHead>{t("gavion.automation.columns.status")}</TableHead>
                <TableHead>{t("gavion.automation.columns.error")}</TableHead>
                <TableHead>{t("gavion.automation.columns.details")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((r) => {
                const expanded = open === r.id;
                const panelId = `run-${r.id}-actions`;
                return (
                  <Fragment key={r.id}>
                    <TableRow>
                      <TableCell>
                        {fmt.format(new Date(r.created_at))}
                      </TableCell>
                      <TableCell>
                        {rules.find((x) => x.id === r.rule_id)?.name ??
                          `#${r.rule_id}`}
                      </TableCell>
                      <TableCell>{statusLabel(r.status)}</TableCell>
                      <TableCell>{safeCode(r.error) ?? "—"}</TableCell>
                      <TableCell>
                        <button
                          type="button"
                          className="underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          aria-expanded={expanded}
                          aria-controls={panelId}
                          onClick={() => setOpen(expanded ? null : r.id)}
                        >
                          {expanded
                            ? t("gavion.automation.history.hide")
                            : t("gavion.automation.columns.details")}
                        </button>
                      </TableCell>
                    </TableRow>
                    {expanded && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <ol
                            id={panelId}
                            className="list-decimal pl-5 text-sm"
                          >
                            {runActions(r.detail).map((a) => (
                              <li key={a.index}>
                                {[
                                  t(`gavion.automation.actions.${a.type}`, {
                                    _: a.type,
                                  }),
                                  statusLabel(a.status),
                                  a.code,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </li>
                            ))}
                          </ol>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
